// apps/smart-shikka-web/src/surfaces/surfaceScaffold.tsx
// PR-CORE-11 — shared scaffold for the non-chat product surfaces.
// Every surface renders inside the canonical ProductShell + AppNav and gets
// the same honest states: loading / error / empty / unavailable (403) /
// preview. No surface invents data — a failed or denied read renders the
// truthful state, never a fake mature panel.
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "../auth.js";
import { AppNav } from "../components/AppNav.js";
import {
  ProductPreferenceControls,
  ProductShell,
  productText,
  useBrandColors,
  type ProductNavRoute,
} from "@smart-shikka/react";
import { useLocale, useTheme } from "../ui.js";

export const surfaceApiBase = () =>
  ((import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL) ??
  window.location.origin;

const CSRF_COOKIE_NAME = "smart_shikka_csrf";
const CSRF_HEADER_NAME = "x-smart-shikka-csrf";

function readCsrfCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  for (const part of document.cookie.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === CSRF_COOKIE_NAME) return decodeURIComponent(rest.join("="));
  }
  return undefined;
}

export function surfaceHeaders(token: string | null | undefined): HeadersInit {
  const csrf = readCsrfCookie();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(csrf ? { [CSRF_HEADER_NAME]: csrf } : {}),
  };
}

export async function surfaceGetJson(
  token: string,
  path: string,
  init?: RequestInit,
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> | null }> {
  const r = await fetch(`${surfaceApiBase()}${path}`, {
    method: "GET",
    headers: surfaceHeaders(token),
    credentials: "include",
    ...init,
  }).catch(() => null);
  if (!r) return { ok: false, status: 0, data: null };
  const data = (await r.json().catch(() => null)) as Record<string, unknown> | null;
  return { ok: r.ok, status: r.status, data };
}

export type SurfaceQueryState<T> =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "unavailable" }
  | { kind: "ready"; data: T };

/** Small canonical fetch hook: one request, honest state machine. */
export function useSurfaceQuery<T>(
  path: string | null,
  map: (data: Record<string, unknown>) => T,
): SurfaceQueryState<T> & { retry: () => void } {
  const { token } = useAuth();
  const [state, setState] = useState<SurfaceQueryState<T>>({ kind: "loading" });
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => {
    setState({ kind: "loading" });
    setNonce((n) => n + 1);
  }, []);
  useEffect(() => {
    if (!token || !path) return;
    let cancelled = false;
    setState({ kind: "loading" });
    surfaceGetJson(token, path)
      .then(({ ok, status, data }) => {
        if (cancelled) return;
        if (!ok || !data) {
          setState(status === 401 || status === 403 ? { kind: "unavailable" } : { kind: "error" });
          return;
        }
        setState({ kind: "ready", data: map(data) });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
    // map/retry are stable-enough deps for this fetch-once-per-path hook.
  }, [token, path, nonce]);
  return { ...state, retry };
}

export function SurfaceStateView(props: { state: SurfaceQueryState<unknown> | { kind: string }; onRetry?: () => void }) {
  const C = useBrandColors();
  const { locale } = useLocale();
  if (props.state.kind === "loading") {
    return <p style={{ color: C.muted, fontSize: 13 }}>{productText("loading", locale)}</p>;
  }
  if (props.state.kind === "unavailable") {
    return <p style={{ color: C.muted, fontSize: 13 }}>{productText("surfaceUnavailable", locale)}</p>;
  }
  if (props.state.kind === "error") {
    return (
      <p style={{ color: C.destructive, fontSize: 13 }}>
        {productText("loadFailed", locale)}
        {props.onRetry ? (
          <button
            type="button"
            onClick={props.onRetry}
            style={{
              marginLeft: 8,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              background: "transparent",
              color: C.fg,
              padding: "2px 10px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            {productText("retry", locale)}
          </button>
        ) : null}
      </p>
    );
  }
  return null;
}

export function EmptyState() {
  const C = useBrandColors();
  const { locale } = useLocale();
  return <p style={{ color: C.muted, fontSize: 13 }}>{productText("emptyList", locale)}</p>;
}

export function SurfacePage(props: {
  route: ProductNavRoute;
  title: string;
  subtitle?: string | undefined;
  children: ReactNode;
}) {
  const { isPrivileged } = useAuth();
  const C = useBrandColors();
  const { locale, setLocale } = useLocale();
  const { resolved: theme, toggle: toggleTheme } = useTheme();
  const [leftPanelOpen, setLeftPanelOpen] = useState(() => {
    try {
      return window.localStorage.getItem("ss.webLeftPanelOpen.v1") !== "0";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("ss.webLeftPanelOpen.v1", leftPanelOpen ? "1" : "0");
    } catch {}
  }, [leftPanelOpen]);

  return (
    <ProductShell
      sidebar={
        <AppNav
          activeRoute={props.route}
          collapsed={!leftPanelOpen}
          onCollapsedChange={(collapsed) => setLeftPanelOpen(!collapsed)}
        />
      }
      topbar={
        <div
          className="ss-product-topbar"
          style={{ height: 52, flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "0 20px" }}
        >
          <strong style={{ fontSize: 14, fontWeight: 650 }}>{props.title}</strong>
          {props.subtitle ? (
            <span style={{ fontSize: 11, color: C.muted }}>{props.subtitle}</span>
          ) : null}
          <div style={{ flex: 1 }} />
          {isPrivileged ? null : null}
          <ProductPreferenceControls
            locale={locale}
            theme={theme}
            onToggleTheme={toggleTheme}
            onToggleLocale={() => setLocale(locale === "hi" ? "en" : "hi")}
          />
        </div>
      }
      canvas={
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "24px 28px", fontFamily: C.fontSans }}>
          {props.children}
        </div>
      }
    />
  );
}
